import { isFirestoreSafeOrganizationId, type OrganizationLocaleCode } from "./organization.js";

export const organizationContentTypeIds = ["imam_profile", "ramadan_message"] as const;
export const organizationContentStatusIds = ["draft", "published", "archived"] as const;
export const organizationContentVisibilityIds = ["connected"] as const;
export const organizationContentSourceIds = [
  "development_seed",
  "experience_twenty_two",
  "migration_hvicc",
  "ramadan_mobile_v1",
] as const;

export type OrganizationContentTypeId = (typeof organizationContentTypeIds)[number];
export type OrganizationContentStatus = (typeof organizationContentStatusIds)[number];
export type OrganizationContentVisibility = (typeof organizationContentVisibilityIds)[number];
export type OrganizationContentSource = (typeof organizationContentSourceIds)[number];

export type OrganizationContentMediaReference = {
  storagePath: string | null;
  altText: string | null;
  altTextAr: string | null;
};

export type OrganizationContentYoutubeReference = {
  canonicalUrl: string | null;
  videoId: string | null;
};

export type OrganizationContentRecord = {
  contentId: string;
  organizationId: string;
  type: OrganizationContentTypeId;
  status: OrganizationContentStatus;
  visibility: OrganizationContentVisibility;
  title: string;
  titleAr: string | null;
  subtitle: string | null;
  subtitleAr: string | null;
  body: string;
  bodyAr: string | null;
  image: OrganizationContentMediaReference;
  galleryImage1?: OrganizationContentMediaReference | null;
  galleryImage2?: OrganizationContentMediaReference | null;
  galleryImage3?: OrganizationContentMediaReference | null;
  youtube?: OrganizationContentYoutubeReference | null;
  sortOrder: number;
  featured: boolean;
  publishedAt: unknown | null;
  createdAt: unknown;
  updatedAt: unknown;
  createdBy: string;
  updatedBy: string;
  source: OrganizationContentSource;
};

export type OrganizationContentValidationResult = {
  valid: boolean;
  errors: string[];
};

export type LocalizedOrganizationContent = {
  title: string;
  subtitle: string | null;
  body: string;
  imageAltText: string | null;
};

const contentIdPattern = /^[a-z0-9][a-z0-9_-]{0,79}$/;
const youtubeVideoIdPattern = /^[A-Za-z0-9_-]{11}$/;

export function organizationContentDocumentPath(organizationId: string, contentId: string): string {
  return `organizations/${organizationId}/content/${contentId}`;
}

export function organizationContentStorageRoot(organizationId: string, contentId: string): string {
  return `organizations/${organizationId}/content/${contentId}/`;
}

export function normalizeOrganizationContentId(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, "")
    .slice(0, 80);
}

export function isFirestoreSafeOrganizationContentId(value: string): boolean {
  return contentIdPattern.test(value);
}

export function isOrganizationContentTypeId(value: unknown): value is OrganizationContentTypeId {
  return (
    typeof value === "string" &&
    organizationContentTypeIds.includes(value as OrganizationContentTypeId)
  );
}

export function isOrganizationContentStatus(value: unknown): value is OrganizationContentStatus {
  return (
    typeof value === "string" &&
    organizationContentStatusIds.includes(value as OrganizationContentStatus)
  );
}

export function isOrganizationContentStoragePath(
  organizationId: string,
  contentId: string,
  value: string | null,
): boolean {
  if (value === null) {
    return true;
  }

  return (
    value.length <= 512 &&
    value.startsWith(organizationContentStorageRoot(organizationId, contentId)) &&
    !value.includes("..") &&
    !value.endsWith("/")
  );
}

export function localizedOrganizationContent(
  content: OrganizationContentRecord,
  locale: OrganizationLocaleCode,
): LocalizedOrganizationContent {
  const useArabic = locale === "ar";

  return {
    body: useArabic ? fallbackText(content.bodyAr, content.body) : content.body,
    imageAltText: useArabic
      ? fallbackNullableText(content.image.altTextAr, content.image.altText)
      : content.image.altText,
    subtitle: useArabic
      ? fallbackNullableText(content.subtitleAr, content.subtitle)
      : content.subtitle,
    title: useArabic ? fallbackText(content.titleAr, content.title) : content.title,
  };
}

export function validateOrganizationContentRecord(
  content: unknown,
): OrganizationContentValidationResult {
  const errors: string[] = [];

  if (!isRecord(content)) {
    return { errors: ["Organization content must be an object."], valid: false };
  }

  const organizationId = stringValue(content.organizationId);
  const contentId = stringValue(content.contentId);

  if (!isFirestoreSafeOrganizationId(organizationId)) {
    errors.push("Organization content organizationId must be Firestore-safe.");
  }

  if (!isFirestoreSafeOrganizationContentId(contentId)) {
    errors.push("Organization content contentId must be Firestore-safe.");
  }

  if (!isOrganizationContentTypeId(content.type)) {
    errors.push("Organization content type must be a stable supported identifier.");
  }

  if (!isOrganizationContentStatus(content.status)) {
    errors.push("Organization content status must be draft, published, or archived.");
  }

  if (content.visibility !== "connected") {
    errors.push("Organization content visibility must be connected.");
  }

  validateRequiredText("English title", content.title, 140, errors);
  validateOptionalText("Arabic title", content.titleAr, 140, errors);
  validateOptionalText("English subtitle", content.subtitle, 180, errors);
  validateOptionalText("Arabic subtitle", content.subtitleAr, 180, errors);
  validateRequiredText("English body", content.body, 2400, errors);
  validateOptionalText("Arabic body", content.bodyAr, 2400, errors);
  validateImageReference(content.image, organizationId, contentId, errors);
  validateOptionalImageReference(
    "Gallery image 1",
    content.galleryImage1,
    organizationId,
    contentId,
    errors,
  );
  validateOptionalImageReference(
    "Gallery image 2",
    content.galleryImage2,
    organizationId,
    contentId,
    errors,
  );
  validateOptionalImageReference(
    "Gallery image 3",
    content.galleryImage3,
    organizationId,
    contentId,
    errors,
  );
  validateYoutubeReference(content.youtube, errors);

  const sortOrder = content.sortOrder;
  if (
    typeof sortOrder !== "number" ||
    !Number.isInteger(sortOrder) ||
    sortOrder < 0 ||
    sortOrder > 10000
  ) {
    errors.push("Organization content sortOrder must be an integer from 0 to 10000.");
  }

  if (typeof content.featured !== "boolean") {
    errors.push("Organization content featured must be a boolean.");
  }

  if (content.status === "published" && content.publishedAt === null) {
    errors.push("Published organization content requires publishedAt.");
  }

  if (content.status !== "published" && content.publishedAt !== null) {
    errors.push("Draft or archived organization content must not carry publishedAt.");
  }

  validateRequiredText("Created by", content.createdBy, 150, errors);
  validateRequiredText("Updated by", content.updatedBy, 150, errors);

  if (!organizationContentSourceIds.includes(content.source as OrganizationContentSource)) {
    errors.push("Organization content source must be supported.");
  }

  return { errors, valid: errors.length === 0 };
}

function validateImageReference(
  image: unknown,
  organizationId: string,
  contentId: string,
  errors: string[],
): void {
  if (!isRecord(image)) {
    errors.push("Organization content image must be a media reference object.");
    return;
  }

  const storagePath = image.storagePath === null ? null : stringValue(image.storagePath);
  if (!isOrganizationContentStoragePath(organizationId, contentId, storagePath)) {
    errors.push("Image storage path must stay under the organization content path.");
  }

  validateOptionalText("Image alt text", image.altText, 160, errors);
  validateOptionalText("Arabic image alt text", image.altTextAr, 160, errors);
}

function validateOptionalImageReference(
  label: string,
  image: unknown,
  organizationId: string,
  contentId: string,
  errors: string[],
): void {
  if (image === undefined || image === null) {
    return;
  }

  const before = errors.length;
  validateImageReference(image, organizationId, contentId, errors);
  if (errors.length > before) {
    errors[before] = `${label}: ${errors[before]}`;
  }
}

function validateYoutubeReference(value: unknown, errors: string[]): void {
  if (value === undefined || value === null) {
    return;
  }

  if (!isRecord(value)) {
    errors.push("YouTube reference must be an object or null.");
    return;
  }

  const canonicalUrl = value.canonicalUrl;
  const videoId = value.videoId;
  if (canonicalUrl === null && videoId === null) {
    return;
  }

  if (
    typeof canonicalUrl !== "string" ||
    typeof videoId !== "string" ||
    !youtubeVideoIdPattern.test(videoId) ||
    canonicalUrl !== `https://www.youtube.com/watch?v=${videoId}`
  ) {
    errors.push("YouTube reference must use a normalized youtube.com watch URL.");
  }
}

function fallbackText(value: string | null, fallback: string): string {
  return value && value.trim().length > 0 ? value : fallback;
}

function fallbackNullableText(value: string | null, fallback: string | null): string | null {
  if (value && value.trim().length > 0) {
    return value;
  }

  return fallback && fallback.trim().length > 0 ? fallback : null;
}

function validateRequiredText(
  label: string,
  value: unknown,
  maxLength: number,
  errors: string[],
): void {
  if (typeof value !== "string" || value.trim().length === 0) {
    errors.push(`${label} is required.`);
    return;
  }

  if (value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}

function validateOptionalText(
  label: string,
  value: unknown,
  maxLength: number,
  errors: string[],
): void {
  if (value === null) {
    return;
  }

  if (typeof value !== "string") {
    errors.push(`${label} must be text or null.`);
    return;
  }

  if (value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
